import React, { useEffect, useState } from "react";
import {
  Modal,
  Typography,
  Space,
  Button,
  Alert,
  Flex,
  Row,
  Col,
  Input,
  Divider,
} from "antd";
import {
  CheckCircleFilled,
  DeleteOutlined,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { CampaignRegisteredChannel } from "../../models/campaign";

const { Title, Text } = Typography;

interface ManageConfigurationModalProps {
  open: boolean;
  onClose: () => void;
  accountEmail: string;
  phones: CampaignRegisteredChannel[] | [];
  onAddNew: () => void;
  onReactivate: (id: number | undefined) => void;
  onDelete: (id: number) => void;
  onUpdateEmail: (email: string) => void;
}

const SectionHeader: React.FC<{ title: string; extra?: React.ReactNode }> = ({
  title,
  extra,
}) => (
  <Flex
    align="center"
    justify="space-between"
    style={{ backgroundColor: "#F1F5F9", padding: "10px 12px" }}
  >
    <Text strong style={{ margin: 0 }}>
      {title}
    </Text>
    {extra}
  </Flex>
);

const statusTag = (status: boolean) =>
  status ? (
    <span
      style={{
        display: "inline-block",
        backgroundColor: "#D1FADF",
        color: "#027A48",
        borderRadius: 4,
        padding: "0px 8px",
        fontSize: 11,
        fontWeight: 500,
        lineHeight: "18px",
      }}
    >
      Active
    </span>
  ) : (
    <span
      style={{
        display: "inline-block",
        backgroundColor: "#FEE2E2",
        color: "#B91C1C",
        borderRadius: 4,
        padding: "0px 8px",
        fontSize: 11,
        fontWeight: 500,
        lineHeight: "18px",
      }}
    >
      Expired
    </span>
  );

const ManageConfigurationModal: React.FC<ManageConfigurationModalProps> = ({
  open,
  onClose,
  accountEmail,
  phones,
  onAddNew,
  onReactivate,
  onDelete,
  onUpdateEmail,
}) => {
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<CampaignRegisteredChannel | null>(null);

  useEffect(() => {
    setEmailModalOpen(false);
    setNewEmail("");
    setEmailError(null);
  }, [accountEmail, open]);

  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const openEmailModal = () => {
    setNewEmail("");
    setEmailError(null);
    setEmailModalOpen(true);
  };

  const handleSaveEmail = () => {
    const trimmed = newEmail.trim();
    if (!trimmed) {
      setEmailError("Email cannot be empty.");
      return;
    }
    if (!isValidEmail(trimmed)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError(null);
    onUpdateEmail(trimmed);
    setEmailModalOpen(false);
  };

  return (
    <>
      <Modal
        className="manage-configuration-modal"
        open={open}
        onCancel={onClose}
        footer={null}
        width={550}
        title={
          <Title level={4} style={{ margin: 0 }}>
            Manage Configuration
          </Title>
        }
      >
        <Alert
          type="info"
          showIcon
          message="WhatsApp sessions are time-limited. Once a session expires, an email notification with reconnection instructions will be sent to the configured email address."
        />
        <div
          style={{
            border: "1px solid #F1F5F9",
            borderRadius: 2,
            marginTop: 15,
          }}
        >
          <SectionHeader title="Account Information" />
          <div style={{ padding: 12 }}>
            <Flex align="center" justify="space-between">
              <Space>
                <MailOutlined />
                <Text>{accountEmail}</Text>
              </Space>
              <Button type="link" style={{ padding: 0 }} onClick={openEmailModal}>
                Change
              </Button>
            </Flex>
          </div>
        </div>

        <div
          style={{
            border: "1px solid #F1F5F9",
            borderRadius: 2,
            margin: "15px 0px",
          }}
        >
          <SectionHeader
            title="Connected Mobile Number"
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={onAddNew}
                disabled={phones.length > 2}
              >
                Add New
              </Button>
            }
          />

          {phones.map((p) => (
            <Row
              key={p.Id}
              gutter={[12, 12]}
              align="middle"
              justify="space-around"
              style={{ padding: 12, borderBottom: "1px solid #F1F5F9" }}
            >
              <Col span={12}>
                <Flex align="flex-start" gap={8}>
                  <PhoneOutlined style={{ marginTop: 4 }} />
                  <div>
                    <Text strong style={{ display: "block" }}>
                      +{p.ContactIdd} {p.ContactNumber}
                    </Text>
                    <div style={{ marginTop: 2 }}>{statusTag(p.Status)}</div>
                  </div>
                </Flex>
              </Col>
              <Col
                span={12}
                style={{
                  justifyContent: "end",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {p.Status ? (
                  <Button
                    type="text"
                    disabled
                    icon={<DeleteOutlined />}
                  />
                ) : (
                  <>
                    <Button
                      type="text"
                      icon={<SyncOutlined style={{ color: "#1677ff" }} />}
                      onClick={() => onReactivate(p.Id)}
                    />
                    <Button
                      type="text"
                      icon={<DeleteOutlined style={{ color: "#DC2626" }} />}
                      onClick={() => setDeleteTarget(p)}
                    />
                  </>
                )}
              </Col>
            </Row>
          ))}
        </div>
      </Modal>

      {/* Change Email Address modal */}
      <Modal
        open={emailModalOpen}
        onCancel={() => setEmailModalOpen(false)}
        footer={null}
        width={520}
        destroyOnClose
        title={
          <Title level={5} style={{ margin: 0 }}>
            Change Email Address
          </Title>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
          message="This email address will be used to send important notifications, including session expiration and reconnection instructions."
        />

        <div style={{ marginBottom: 16 }}>
          <Text style={{ display: "block", marginBottom: 6 }}>
            Current Email Address
          </Text>
          <Input
            value={accountEmail}
            disabled
            suffix={<CheckCircleFilled style={{ color: "#12B76A" }} />}
          />
        </div>

        <div>
          <Text style={{ display: "block", marginBottom: 6 }}>
            New Email Address<span style={{ color: "#DC2626" }}>*</span>
          </Text>
          <Input
            placeholder="Enter new email address"
            value={newEmail}
            onChange={(e) => {
              setNewEmail(e.target.value);
              setEmailError(null);
            }}
            onPressEnter={handleSaveEmail}
            status={emailError ? "error" : undefined}
          />
          {emailError && (
            <Text type="danger" style={{ fontSize: 12 }}>
              {emailError}
            </Text>
          )}
        </div>

        <Divider />
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Button
              size="large"
              style={{ width: "100%" }}
              onClick={() => setEmailModalOpen(false)}
            >
              Cancel
            </Button>
          </Col>
          <Col span={12}>
            <Button
              size="large"
              type="primary"
              style={{ width: "100%" }}
              disabled={!newEmail.trim()}
              onClick={handleSaveEmail}
            >
              Save Changes
            </Button>
          </Col>
        </Row>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        open={deleteTarget !== null}
        onCancel={() => setDeleteTarget(null)}
        footer={null}
        centered
        width={520}
        destroyOnClose
        closable={false}
      >
        <div
          style={{
            display: "grid",
            placeItems: "center",
            gap: 12,
            paddingTop: 8,
          }}
        >
          <i className="icon-trash-red" />
          <h3 style={{ margin: 0, fontSize: 20 }}>Delete Mobile Number</h3>
          <p style={{ margin: 0, color: "#6b7280", textAlign: "center" }}>
            Are you sure you want to remove{" "}
            <Text strong>
              +{deleteTarget?.ContactIdd} {deleteTarget?.ContactNumber}
            </Text>{" "}
            from this account?
          </p>
        </div>
        <Divider />
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Button
              size="large"
              style={{ width: "100%" }}
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button>
          </Col>
          <Col span={12}>
            <Button
              size="large"
              danger
              type="primary"
              style={{ width: "100%" }}
              onClick={() => {
                if (deleteTarget?.Id != null) onDelete(deleteTarget.Id);
                setDeleteTarget(null);
              }}
            >
              Delete Number
            </Button>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default ManageConfigurationModal;
